import {
  Avatar,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  makeStyles,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@material-ui/core';
import { green } from '@material-ui/core/colors';
import { Check } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import {
  HelpPopper,
  PageableTable,
  PopperDropdownMenu,
  SnackbarContext,
} from 'components';
import { PageableTableHeader } from 'components/PageableTable/components';
import {
  useLocalStorageSettings,
  TableSettings,
  getTableSettingsStorageKey,
} from 'hooks';
import {
  actionSuggestion,
  fetchSuggestionsForCompany,
  fetchMergePreview,
} from 'lib/Api/Matches';
import { getUserData } from 'lib/Helper/Auth';
import { stringAvatar } from 'lib/Helper/Avatars';
import { PagedSuggestions, Suggestion } from 'lib/Model/Suggestion';
import { MergePreview } from 'lib/Model/Merge';
import * as React from 'react';
import { useLocation } from 'react-router-dom';

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  contactColumn: {},
  contactsColumn: {
    width: 135,
    textAlign: 'right',
    paddingLeft: 0,
  },
  smallColumn: {
    width: 50,
    textAlign: 'right',
    paddingLeft: 0,
  },
  avatarColumn: {
    width: 30,
    paddingRight: 0,
    verticalAlign: 'top',
    paddingTop: theme.spacing(3),
  },
  smallAvatar: {
    width: 26,
    height: 26,
    fontSize: 14,
  },
  warningAlert: {
    marginBottom: theme.spacing(2),
  },
  clusterSection: {
    marginBottom: theme.spacing(2),
  },
  clusterTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(1),
  },
  clusterMember: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
  },
  conflictChip: {
    backgroundColor: theme.palette.error.light,
    color: 'black',
    fontWeight: 'bold',
  },
  safeChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  previewLoading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
}));

interface Props {
  companyId: string;
  doCompanyRefresh(): void;
}

export const SuggestedMatches: React.FC<Props> = ({
  companyId,
  doCompanyRefresh,
}) => {
  const [loading, setLoading] = React.useState(false);
  const classes = useStyles();
  const [pageNumber, setPageNumber] = React.useState(0);
  const [confirmMergeOpen, setConfirmMergeOpen] = React.useState(false);
  const [selectedSuggestion, setSelectedSuggestion] =
    React.useState<Suggestion | null>(null);
  const { pathname, hash } = useLocation();
  const [tableSettings, setTableSettings] =
    useLocalStorageSettings<TableSettings>(
      getTableSettingsStorageKey(pathname, hash),
      { rowsPerPage: 25 },
    );

  const snackbar = React.useContext(SnackbarContext);
  const [data, setData] = React.useState<PagedSuggestions | undefined>();
  const [mergePreview, setMergePreview] = React.useState<MergePreview | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = React.useState(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const user = getUserData();

  const fetchData = React.useCallback(() => {
    setLoading(true);
    fetchSuggestionsForCompany(
      companyId,
      pageNumber + 1,
      tableSettings.rowsPerPage,
    ).then((d) => {
      setLoading(false);
      setData(d);
    });
  }, [setLoading, setData, pageNumber, tableSettings.rowsPerPage, companyId]);

  React.useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: PageableTableHeader[] = [
    {
      key: 'avatar',
      label: '',
      sortable: false,
    },
    {
      key: 'contact',
      label: 'Contact',
      sortable: false,
    },
    {
      key: 'contacts',
      label: '',
      sortable: false,
    },
  ];

  async function onAccept(d: Suggestion) {
    setSelectedSuggestion(d);
    setPreviewLoading(true);
    setConfirmMergeOpen(true);
    setMergePreview(null);

    try {
      const preview = await fetchMergePreview(companyId, d.suggestion_id);
      setMergePreview(preview);
    } catch (error) {
      setMergePreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleConfirmMerge(force: boolean = false) {
    if (!selectedSuggestion) return;

    setLoading(true);
    try {
      await actionSuggestion(
        companyId,
        selectedSuggestion.suggestion_id,
        'merge',
        force,
        selectedSuggestion.alias_id,
      );
      snackbar.success('Companies merged successfully.');
      doCompanyRefresh();
    } catch (ex: any) {
      if (ex?.response?.status === 409 && !force) {
        const detail = ex?.response?.data?.detail;
        const reason = detail?.reason;

        if (reason === 'stale_suggestion') {
          snackbar.error(
            'Suggestions have been refreshed since this page was loaded. Refresh the page and try again.',
          );
        } else if (reason === 'cluster_merge_risk') {
          snackbar.error(
            'Merge blocked because this would join two existing clusters. Review both clusters and use "Merge Anyway" to override.',
          );
        } else if (reason === 'platform_conflict') {
          snackbar.error(
            'Merge blocked due to platform conflict. Use "Merge Anyway" to override.',
          );
        } else {
          snackbar.error(detail?.message || 'Merge blocked.');
        }
      } else {
        snackbar.error(`Failed to merge companies: ${ex}`);
      }
    } finally {
      setLoading(false);
      setConfirmMergeOpen(false);
      setSelectedSuggestion(null);
      setMergePreview(null);
    }
  }

  const hasPlatformConflict = Boolean(mergePreview?.has_conflict);
  const hasClusterMergeRisk = Boolean(mergePreview?.has_cluster_merge_risk);
  const hasMergeRisk = hasPlatformConflict || hasClusterMergeRisk;

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          title={`Suggested matching profiles`}
          subheader={
            <span>
              Suggested companies potentially identical to this one.
              {user?.permissions?.includes('merge') && (
                <span>
                  <br />
                  Click the tick button if you are sure the suggested company
                  below is <strong>IDENTICAL</strong> to this one.
                </span>
              )}
            </span>
          }
          action={
            <HelpPopper style={{ padding: 8 }}>
              <Typography variant="body2" gutterBottom>
                Every day, suggested similar profiles to this company will be
                generated.
              </Typography>
              <Typography variant="body2" gutterBottom>
                The original platform is displayed as well as what the
                suggestion is based on.
              </Typography>
              <Typography variant="body2" gutterBottom>
                Suggestions are based on exact name matches, matching contact
                emails, matching contact email domains, or matching registration
                numbers.
              </Typography>
              <Typography variant="body2" gutterBottom>
                If you know a company that should be suggested but isn't, please
                reach out to a member from the insights team.
              </Typography>
            </HelpPopper>
          }
        />
        <Divider />
        <PageableTable
          tableSettings={tableSettings}
          setTableSettings={setTableSettings}
          showSkeletonLoading
          hideColumns
          rows={
            data?.data?.map((d, index) => {
              const menuItems = [
                {
                  label: 'Accept merge',
                  onClick: () => onAccept(d),
                  icon: Check,
                  iconColor: green[500],
                },
              ];
              return {
                key: index.toString(),
                cells: [
                  {
                    key: 'avatar',
                    display: d.alias_company_name && (
                      <Avatar
                        {...stringAvatar(d.alias_company_name)}
                        variant="rounded"
                        className={classes.smallAvatar}
                      />
                    ),
                    props: {
                      className: classes.avatarColumn,
                    },
                  },
                  {
                    key: 'contact',
                    display: (
                      <React.Fragment>
                        <Button
                          variant="text"
                          color="primary"
                          style={{
                            padding: 0,
                            textAlign: 'left',
                            justifyContent: 'flex-start',
                          }}
                          onClick={() => onOpenDetail(d)}
                        >
                          {d.alias_company_name}
                        </Button>
                        <div>
                          <Typography variant="caption">
                            {d.alias_cnumber
                              ? `C-Number: ${d.alias_cnumber}, `
                              : ''}
                            {d.alias_vat_number
                              ? `VAT Number: ${d.alias_vat_number}`
                              : ''}
                          </Typography>
                        </div>
                        <div>
                          <Typography variant="caption">
                            {resolvePlatformName(d.alias_id)}: {d.match_type}{' '}
                            match
                          </Typography>
                        </div>
                      </React.Fragment>
                    ),
                  },
                  {
                    key: 'contacts',
                    display:
                      user?.permissions?.includes('merge') &&
                      (isDesktop ? (
                        <ButtonGroup disableElevation>
                          {menuItems.map((m, menuItemIndex) => (
                            <Tooltip
                              title={m.label}
                              key={`button_${index}_${menuItemIndex}`}
                            >
                              <Button
                                size="small"
                                style={{
                                  color: m.iconColor,
                                }}
                                onClick={m.onClick}
                              >
                                <m.icon />
                              </Button>
                            </Tooltip>
                          ))}
                        </ButtonGroup>
                      ) : (
                        <PopperDropdownMenu menuItems={menuItems} />
                      )),
                    props: {
                      className: isDesktop
                        ? classes.contactsColumn
                        : classes.smallColumn,
                    },
                  },
                ],
              };
            }) ?? []
          }
          loading={loading}
          pageNumber={pageNumber}
          rowCount={data?.total_count ?? 0}
          columns={columns}
          onChangePage={setPageNumber}
        />
      </Card>
      <Dialog
        open={confirmMergeOpen}
        onClose={() => {
          setConfirmMergeOpen(false);
          setMergePreview(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Merge</DialogTitle>
        <DialogContent>
          {previewLoading ? (
            <div className={classes.previewLoading}>
              <CircularProgress />
            </div>
          ) : (
            <>
              {mergePreview?.has_conflict && (
                <Alert severity="error" className={classes.warningAlert}>
                  <strong>Platform conflict detected!</strong> Merging will
                  create duplicate platforms in the same cluster. This is
                  usually a mistake. Review the details below carefully.
                </Alert>
              )}

              {mergePreview?.has_cluster_merge_risk && (
                <Alert severity="warning" className={classes.warningAlert}>
                  <strong>Cluster merge risk detected.</strong> This action will
                  merge two existing clusters together. Review both clusters
                  carefully before continuing.
                </Alert>
              )}

              {mergePreview && (
                <div className={classes.clusterSection}>
                  <Typography
                    variant="subtitle2"
                    className={classes.clusterTitle}
                  >
                    Current cluster ({mergePreview.target_cluster.length}{' '}
                    profile{mergePreview.target_cluster.length !== 1 ? 's' : ''}
                    ):
                  </Typography>
                  {mergePreview.target_cluster.map((member, i) => {
                    const isConflict = mergePreview.conflicts.some((c) =>
                      c.existing_companies.some(
                        (e) => e.company_id === member.company_id,
                      ),
                    );
                    return (
                      <div key={i} className={classes.clusterMember}>
                        <Chip
                          size="small"
                          label={resolvePlatformNameFromPrefix(
                            member.platform_prefix.toUpperCase(),
                          )}
                          className={
                            isConflict ? classes.conflictChip : classes.safeChip
                          }
                        />
                        <Typography variant="body2">
                          {member.company_title || member.company_id}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              )}

              {mergePreview && (
                <div className={classes.clusterSection}>
                  <Typography
                    variant="subtitle2"
                    className={classes.clusterTitle}
                  >
                    Will be added ({mergePreview.incoming_cluster.length}{' '}
                    profile
                    {mergePreview.incoming_cluster.length !== 1 ? 's' : ''}):
                  </Typography>
                  {mergePreview.incoming_cluster.map((member, i) => {
                    const isConflict = mergePreview.conflicts.some(
                      (c) =>
                        c.incoming_company.company_id === member.company_id,
                    );
                    return (
                      <div key={i} className={classes.clusterMember}>
                        <Chip
                          size="small"
                          label={resolvePlatformNameFromPrefix(
                            member.platform_prefix.toUpperCase(),
                          )}
                          className={
                            isConflict ? classes.conflictChip : classes.safeChip
                          }
                        />
                        <Typography variant="body2">
                          {member.company_title || member.company_id}
                        </Typography>
                      </div>
                    );
                  })}
                </div>
              )}

              {mergePreview?.has_conflict && (
                <Alert severity="warning" className={classes.warningAlert}>
                  Conflicting platform
                  {mergePreview.conflicts.length > 1 ? 's' : ''}:{' '}
                  {[
                    ...new Set(
                      mergePreview.conflicts.map((c) =>
                        resolvePlatformNameFromPrefix(
                          c.platform_prefix.toUpperCase(),
                        ),
                      ),
                    ),
                  ].join(', ')}
                </Alert>
              )}

              {!mergePreview?.has_conflict && (
                <Typography>
                  Are you sure you want to merge{' '}
                  {selectedSuggestion?.alias_company_name} into this cluster?
                  This action can have important consequences on processes such
                  as PROACT, revenue calculations and more.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmMergeOpen(false);
              setMergePreview(null);
            }}
          >
            Cancel
          </Button>
          {hasMergeRisk ? (
            <Button
              onClick={() => handleConfirmMerge(true)}
              color="secondary"
              disabled={loading || previewLoading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Merge Anyway (Override)
            </Button>
          ) : (
            <Button
              onClick={() => handleConfirmMerge(false)}
              color="primary"
              disabled={loading || previewLoading}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Confirm Merge
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );

  function onOpenDetail(d: Suggestion) {
    window.open(`/clients/${d.alias_id}`);
  }
};

export function resolvePlatformName(alias_id: string) {
  if (!alias_id) {
    return '';
  }
  const parts = alias_id.toUpperCase().split('-');
  if (parts.length < 1) {
    return alias_id;
  }
  return resolvePlatformNameFromPrefix(parts[0]);
}

export function resolvePlatformNameFromPrefix(prefix: string) {
  switch (prefix.toUpperCase()) {
    case 'KNT':
      return 'Konnekt ATS';
    case 'TLX':
      return 'Talexio';
    case 'JIM':
      return 'jobsinmalta';
    case 'PM':
      return 'Konnekt Payroll';
    case 'PM-':
      return 'Konnekt Payroll';
    case 'KMP':
      return 'KeepMePosted';
    case 'XRO':
      return 'Xero';
    case 'XRI':
      return 'Xero (ITV)';
    case 'XRT':
      return 'Xero (Talexio)';
    case 'XRK':
      return 'Xero (Konnekt)';
    case 'SIM':
      return 'Salariesinmalta';
    case 'ZND':
      return 'Zendesk Sell';
    case 'ALF':
      return 'Alfred Jobs';
    case 'ZNS':
      return 'Zendesk Support';
    case 'MND':
      return 'Monday.com';
    case 'ZENDESK':
      return 'Zendesk Sell';
    case 'ZENDESK_SUPPORT':
      return 'Zendesk Support';
    default:
      return prefix;
  }
}

export function resolveMergedAliasesToPlatformNames(
  merged_aliases: string | null,
) {
  if (!merged_aliases) {
    return [];
  }
  const aliases = merged_aliases.toUpperCase().split(',');
  return aliases.map((alias) => resolvePlatformNameFromPrefix(alias));
}
